/* ────────────────────────────────────────────────────────────
   Workflow Builder — Hyarchis design system
   Brand-purple primary, magenta gradient navbar, business density.
   ──────────────────────────────────────────────────────────── */

:root {
  /* Brand / raw color */
  --brand-purple-100: #3D2F84;
  --brand-purple-50:  rgba(61, 47, 132, 0.50);
  --brand-purple-25:  rgba(61, 47, 132, 0.25);
  --brand-gradient-1: #422E83;
  --brand-gradient-2: #713676;
  --brand-gradient-3: #8F3B6D;
  --brand-gradient: linear-gradient(to right, var(--brand-gradient-1) 0%, var(--brand-gradient-2) 60%, var(--brand-gradient-3) 100%);

  --color-blue:    #0073DD;
  --color-red:     #DC3545;
  --color-orange:  #FD7E14;
  --color-yellow:  #FFC107;
  --color-green:   #28A745;
  --color-cyan:    #17A2B8;

  --white:    #FFFFFF;
  --gray-100: #F8F9FA;
  --gray-200: #E9ECEF;
  --gray-300: #DEE2E6;
  --gray-400: #CED4DA;
  --gray-500: #ADB5BD;
  --gray-600: #868E96;
  --gray-700: #495057;
  --gray-800: #343A40;
  --gray-900: #212529;

  --surface-card-header: #F5F5F5;
  --surface-card-border: #E5E5E5;
  --surface-error-glow: #FDE6E8;

  /* Semantic */
  --bg-page: var(--white);
  --bg-app:  var(--gray-100);
  --bg-card: var(--white);
  --fg-1: var(--gray-900);
  --fg-2: var(--gray-700);
  --fg-muted: var(--gray-600);
  --fg-on-dark: var(--white);
  --fg-placeholder: #999999;

  --border-default: var(--gray-300);
  --border-card: var(--surface-card-border);
  --border-divider: var(--gray-200);

  --primary: var(--brand-purple-100);
  --primary-on: var(--white);
  --primary-hover: #322670;
  --link: var(--brand-purple-100);

  --focus-ring: var(--brand-purple-25);
  --focus-border: var(--brand-purple-100);
  --invalid: var(--color-red);

  /* Status (legacy alias) */
  --color-text: var(--fg-1);
  --color-text-muted: var(--fg-muted);
  --color-accent: var(--primary);
  --color-border: var(--border-default);
  --color-border-light: var(--border-divider);
  --color-surface: var(--gray-100);
  --color-danger: var(--color-red);
  --color-danger-bg: #fdf0ef;
  --color-success: var(--color-green);
  --color-bg: var(--bg-page);

  /* Type */
  --font-sans: 'Mulish', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  --font-mono: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  --font: var(--font-sans);

  /* Radii */
  --radius-sm: 3px;
  --radius-base: 4px;
  --radius-md: 6px;
  --radius-card: 8px;
  --radius-pill: 999px;

  /* Shadows */
  --shadow-sm: 0 1px 2px rgba(20, 15, 60, 0.06);
  --shadow-md: 0 4px 12px rgba(20, 15, 60, 0.08);
  --shadow-lg: 0 8px 24px rgba(20, 15, 60, 0.12);
  --shadow-focus: 0 0 0 3px var(--focus-ring);
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

html, body, #root { height: 100%; margin: 0; }

body {
  margin: 0;
  font-family: var(--font-sans);
  font-size: 14px;
  color: var(--fg-1);
  background: var(--bg-app);
  -webkit-font-smoothing: antialiased;
  overflow: hidden;
}

h1, h2, h3, h4, h5, h6 {
  font-family: var(--font-sans);
  font-weight: 700;
  letter-spacing: -0.005em;
  margin: 0 0 0.75rem;
  color: var(--fg-1);
}

h1 { font-size: 28px; font-weight: 700; }
h3 { font-size: 16px; font-weight: 700; }
h4 { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--fg-muted); margin: 0 0 0.5rem; }

code, pre { font-family: var(--font-mono); }

a { color: var(--link); text-decoration: none; }
a:hover { text-decoration: underline; }

input[type="text"],
input[type="password"],
input[type="number"],
input[type="search"],
select,
textarea {
  font-family: var(--font-sans);
  font-size: 13px;
  padding: 0.4rem 0.625rem;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-base);
  color: var(--fg-1);
  background: var(--white);
  outline: none;
  line-height: 1.4;
  transition: border-color 0.12s, box-shadow 0.12s;
}

input[type="text"]:focus,
input[type="password"]:focus,
input[type="number"]:focus,
input[type="search"]:focus,
select:focus,
textarea:focus {
  border-color: var(--focus-border);
  box-shadow: var(--shadow-focus);
}

input:disabled,
select:disabled {
  background: var(--gray-100);
  color: var(--fg-muted);
  cursor: not-allowed;
}

::placeholder { color: var(--fg-placeholder); }

/* ──────────────── Buttons ──────────────── */

button {
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 600;
  padding: 0.4rem 0.875rem;
  border-radius: var(--radius-base);
  border: 1px solid var(--primary);
  background: var(--primary);
  color: var(--white);
  cursor: pointer;
  line-height: 1.4;
  white-space: nowrap;
  transition: background 0.12s, border-color 0.12s, color 0.12s, opacity 0.12s, box-shadow 0.12s;
}

button:hover:not(:disabled) {
  background: var(--primary-hover);
  border-color: var(--primary-hover);
}

button:focus-visible {
  outline: 0;
  box-shadow: var(--shadow-focus);
}

button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

button.btn-secondary {
  background: var(--white);
  color: var(--fg-1);
  border-color: var(--border-default);
}
button.btn-secondary:hover:not(:disabled) {
  background: var(--gray-100);
  border-color: var(--gray-400);
  color: var(--fg-1);
}

button.btn-ghost {
  background: transparent;
  color: var(--fg-2);
  border-color: transparent;
}
button.btn-ghost:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.05);
  color: var(--fg-1);
}

button.btn-danger {
  background: var(--white);
  color: var(--color-red);
  border-color: var(--color-red);
}
button.btn-danger:hover:not(:disabled) {
  background: var(--color-danger-bg);
}

button.btn-success {
  background: var(--color-green);
  border-color: var(--color-green);
  color: var(--white);
}
button.btn-success:hover:not(:disabled) {
  background: #218838;
  border-color: #218838;
}

button.btn-sm {
  font-size: 12px;
  padding: 0.25rem 0.625rem;
}

button.btn-xs {
  font-size: 11.5px;
  padding: 0.2rem 0.5rem;
}

/* ──────────────── Nav (gradient top bar) ──────────────── */

.app-navbar {
  flex-shrink: 0;
  height: 56px;
  background: var(--brand-gradient);
  color: var(--fg-on-dark);
  display: flex;
  align-items: center;
  padding: 0 18px;
  gap: 24px;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.06);
  z-index: 50;
}

.app-navbar .brand {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.app-navbar .brand img {
  height: 28px;
  /* logo-white.svg ships without fills — force white */
  filter: brightness(0) invert(1);
}

.app-navbar .brand .product {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  opacity: 0.85;
  padding-left: 12px;
  border-left: 1px solid rgba(255, 255, 255, 0.25);
  white-space: nowrap;
}

.app-navbar .nav-context {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  margin-left: 8px;
  font-size: 13.5px;
}

.app-navbar .nav-context .sep {
  width: 1px;
  height: 22px;
  background: rgba(255, 255, 255, 0.25);
  flex-shrink: 0;
}

.app-navbar .nav-context .crumb {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 4px;
  opacity: 0.9;
  background: rgba(255, 255, 255, 0.1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;
}

.app-navbar .nav-context .wf-id {
  font-family: var(--font-mono);
  font-size: 11.5px;
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 240px;
}

.app-navbar .nav-context .unsaved {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 2px 7px;
  border-radius: 999px;
  background: rgba(255, 198, 88, 0.25);
  color: #ffd98a;
}

.app-navbar .right {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  opacity: 0.95;
}

.app-navbar button.nav-btn {
  background: rgba(255, 255, 255, 0.0);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: var(--white);
  font-weight: 600;
  font-size: 12.5px;
  padding: 0.35rem 0.75rem;
  border-radius: var(--radius-base);
}

.app-navbar button.nav-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.4);
}

.app-navbar button.nav-btn.active {
  background: rgba(255, 255, 255, 0.22);
  border-color: rgba(255, 255, 255, 0.45);
}

.app-navbar button.nav-btn.primary {
  background: var(--color-green);
  border-color: var(--color-green);
  color: #fff;
}
.app-navbar button.nav-btn.primary:hover:not(:disabled) {
  background: #218838;
  border-color: #218838;
}

.app-navbar button.icon-btn {
  background: transparent;
  border: 1px solid transparent;
  color: var(--white);
  width: 30px;
  height: 30px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-base);
  opacity: 0.85;
}
.app-navbar button.icon-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.15);
  opacity: 1;
}

/* ──────────────── Editor toolbar (below navbar) ──────────────── */

.editor-toolbar {
  flex-shrink: 0;
  height: 48px;
  background: var(--white);
  border-bottom: 1px solid var(--border-card);
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 10px;
  z-index: 10;
}

.editor-toolbar .group {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.editor-toolbar .toolbar-spacer { flex: 1; }

.editor-toolbar .divider {
  width: 1px;
  height: 22px;
  background: var(--border-divider);
  flex-shrink: 0;
}

.editor-toolbar button.tool-btn {
  background: transparent;
  border: 1px solid transparent;
  color: var(--fg-2);
  padding: 0.35rem 0.6rem;
  font-size: 12.5px;
  font-weight: 600;
  border-radius: var(--radius-base);
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.editor-toolbar button.tool-btn:hover:not(:disabled) {
  background: var(--gray-100);
  color: var(--fg-1);
}

.editor-toolbar button.tool-btn.active {
  background: var(--brand-purple-25);
  color: var(--primary);
}

.editor-toolbar button.tool-icon {
  background: transparent;
  border: 1px solid transparent;
  color: var(--fg-2);
  padding: 0.3rem 0.4rem;
  border-radius: var(--radius-base);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.editor-toolbar button.tool-icon:hover:not(:disabled) {
  background: var(--gray-100);
  color: var(--primary);
}

.editor-toolbar select.tool-select {
  height: 30px;
  padding: 0 0.5rem;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-base);
  font-size: 12px;
  background: var(--white);
  color: var(--fg-1);
}

.editor-toolbar .run-btn {
  background: var(--color-green);
  border-color: var(--color-green);
  color: #fff;
  font-weight: 700;
  padding: 0.4rem 0.875rem;
  font-size: 13px;
  border-radius: var(--radius-base);
}
.editor-toolbar .run-btn.active,
.editor-toolbar .run-btn:hover:not(:disabled) {
  background: #1e7e34;
  border-color: #1e7e34;
}

.editor-toolbar .primary-btn {
  background: var(--primary);
  border-color: var(--primary);
  color: #fff;
  padding: 0.4rem 0.875rem;
  font-size: 13px;
  border-radius: var(--radius-base);
}
.editor-toolbar .primary-btn:hover:not(:disabled) {
  background: var(--primary-hover);
  border-color: var(--primary-hover);
}

/* ──────────────── Side panel (right) ──────────────── */

.side-panel {
  width: 340px;
  flex-shrink: 0;
  background: var(--white);
  border-left: 1px solid var(--border-card);
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.side-panel .tabs {
  display: flex;
  border-bottom: 1px solid var(--border-card);
  padding: 0 8px;
  flex-shrink: 0;
}

.side-panel .tabs button {
  flex: 1;
  background: transparent;
  border: 0;
  border-bottom: 2px solid transparent;
  padding: 0.65rem 0.5rem;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--fg-muted);
  cursor: pointer;
  border-radius: 0;
  margin-bottom: -1px;
}

.side-panel .tabs button:hover {
  color: var(--fg-1);
  background: transparent;
}

.side-panel .tabs button.active {
  color: var(--primary);
  border-bottom-color: var(--primary);
}

.side-panel .body {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
}

/* ──────────────── Action palette (left rail) ──────────────── */

.action-palette-rail {
  width: 240px;
  flex-shrink: 0;
  background: var(--white);
  border-right: 1px solid var(--border-card);
  padding: 1rem;
  overflow-y: auto;
}

/* ──────────────── Status footer ──────────────── */

.status-footer {
  padding: 0.5rem 1.25rem;
  background: var(--white);
  border-top: 1px solid var(--border-card);
  font-size: 12.5px;
  min-height: 32px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.status-footer.error  { color: var(--color-red); font-weight: 600; }
.status-footer.success { color: #155724; font-weight: 600; }

/* ──────────────── xyflow tweaks ──────────────── */

.react-flow__node .add-next-btn {
  opacity: 0;
  transition: opacity 0.15s, border-color 0.15s, color 0.15s, box-shadow 0.15s;
}
.react-flow__node:hover .add-next-btn,
.react-flow__node .add-next-btn:focus {
  opacity: 1;
}
.react-flow__node .add-next-btn:hover {
  border-color: var(--primary) !important;
  color: var(--primary) !important;
  box-shadow: 0 0 0 2px var(--brand-purple-25);
}

.react-flow__edge.selected .react-flow__edge-path {
  stroke: var(--primary) !important;
  stroke-width: 2.4 !important;
}
.react-flow__edge.selected { z-index: 1000 !important; }

.react-flow__background {
  background-color: #f4f5f8;
}

/* Selected node highlight (xyflow tags the wrapper with .selected) */
.react-flow__node.selected > div {
  /* boxShadow is also set inline in WorkflowNode for the inner box; this
     reinforces it for any inner div that doesn't get the prop. */
}

/* Blue glowing outline around the canvas while a processing job is running */
@keyframes canvas-running-pulse {
  0%   { box-shadow: inset 0 0 0 3px rgba(0, 115, 221, 0.45); }
  50%  { box-shadow: inset 0 0 0 5px rgba(0, 115, 221, 0.75); }
  100% { box-shadow: inset 0 0 0 3px rgba(0, 115, 221, 0.45); }
}
main.canvas-running::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 5;
  border-radius: 4px;
  animation: canvas-running-pulse 1.6s ease-in-out infinite;
}

/* Editable workflow-name crumb in the top nav */
.app-navbar .nav-context input.crumb-edit {
  appearance: none;
  background: rgba(255, 255, 255, 0.10);
  border: 1px solid rgba(255, 255, 255, 0.0);
  color: var(--white);
  font-weight: 600;
  font-size: 13.5px;
  font-family: var(--font-sans);
  padding: 5px 10px;
  border-radius: 4px;
  outline: none;
  max-width: 320px;
  transition: background 0.12s, border-color 0.12s;
}
.app-navbar .nav-context input.crumb-edit:hover {
  background: rgba(255, 255, 255, 0.18);
}
.app-navbar .nav-context input.crumb-edit:focus {
  background: rgba(255, 255, 255, 0.22);
  border-color: rgba(255, 255, 255, 0.4);
  box-shadow: none;
}

/* ──────────────── Misc ──────────────── */

.label-caps {
  display: block;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-muted);
  margin-bottom: 6px;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
  background: var(--gray-100);
  color: var(--fg-2);
}
.badge.primary { background: var(--brand-purple-25); color: var(--primary); }
.badge.warn    { background: #fff3cd; color: #856404; }
.badge.danger  { background: #f8d7da; color: #721c24; }
